import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import styles from './ChatSyncorpPage.module.css';

/**
 * Player de áudio do Laboratório: play/pause, duração, tempo decorrido,
 * barra com seek e estado visual durante a reprodução. Aceita um src pronto
 * (gravação local ou resposta falada) ou um uidAudio — nesse caso busca os
 * bytes autenticados em /web/audio/:uid (um <audio src> não envia o token).
 * A transcrição fica recolhível por "Ver transcrição".
 *
 * Instância única: este <audio> é o ÚNICO responsável pela reprodução da
 * mensagem — inclusive o autoplay da resposta falada (`autoPlay`). Só um
 * áudio toca por vez em toda a tela: ao iniciar outro, o anterior é pausado;
 * `pararAudioAtivo()` interrompe o que estiver tocando (sair/trocar/encerrar/
 * reiniciar conversa).
 */
type Props = {
  src?: string;
  uidAudio?: string;
  transcricao?: string;
  falhaTranscricao?: boolean;
  /** Inicia sozinho ao montar (resposta falada recém-chegada). */
  autoPlay?: boolean;
};

let audioAtivo: HTMLAudioElement | null = null;

export function pararAudioAtivo(): void {
  if (audioAtivo) audioAtivo.pause();
  audioAtivo = null;
}

function formatarTempo(segundos: number): string {
  if (!Number.isFinite(segundos) || segundos < 0) return '0:00';
  const total = Math.floor(segundos);
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

const PlayerAudio: React.FC<Props> = ({ src, uidAudio, transcricao, falhaTranscricao, autoPlay }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [srcCarregado, setSrcCarregado] = useState<string | undefined>(src);
  const [tocando, setTocando] = useState(false);
  const [duracao, setDuracao] = useState(0);
  const [atual, setAtual] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [transcricaoAberta, setTranscricaoAberta] = useState(false);

  // Áudio guardado no servidor: baixa com o token e cria uma URL local.
  useEffect(() => {
    if (src || !uidAudio) return;
    let url: string | undefined;
    let cancelado = false;
    api.get(`/web/audio/${uidAudio}`, { responseType: 'blob' })
      .then((resp) => {
        if (cancelado) return;
        url = URL.createObjectURL(resp.data);
        setSrcCarregado(url);
      })
      .catch(() => { if (!cancelado) setErro('Não foi possível carregar o áudio.'); });
    return () => {
      cancelado = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src, uidAudio]);

  // Autoplay pelo MESMO <audio> do player (uma vez por instância). Se o
  // browser bloquear, nada quebra: o botão ▶ continua valendo.
  const autoplayFeitoRef = useRef(false);
  useEffect(() => {
    const el = audioRef.current;
    if (!autoPlay || !srcCarregado || !el || autoplayFeitoRef.current) return;
    autoplayFeitoRef.current = true;
    el.play().catch(() => {});
  }, [autoPlay, srcCarregado]);

  // Ao desmontar (trocou/saiu da conversa), não deixa som órfão.
  // (O ref do React já vem limpo no cleanup: guarda o elemento à parte.)
  const elementoRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    elementoRef.current = audioRef.current;
  });
  useEffect(() => () => {
    const el = elementoRef.current;
    if (el) {
      el.pause();
      if (audioAtivo === el) audioAtivo = null;
    }
  }, []);

  const aoIniciar = () => {
    const el = audioRef.current;
    if (el) {
      if (audioAtivo && audioAtivo !== el) audioAtivo.pause();
      audioAtivo = el;
    }
    setTocando(true);
  };

  const aoParar = () => {
    if (audioAtivo === audioRef.current) audioAtivo = null;
    setTocando(false);
  };

  const aoCarregarMetadados = () => {
    const el = audioRef.current;
    if (!el) return;
    if (Number.isFinite(el.duration)) {
      setDuracao(el.duration);
      return;
    }
    // webm/opus gravado pelo MediaRecorder costuma vir sem duração: força o
    // navegador a calcular levando o ponteiro pro fim e voltando.
    el.currentTime = 1e101;
    const ajustar = () => {
      el.removeEventListener('timeupdate', ajustar);
      setDuracao(Number.isFinite(el.duration) ? el.duration : 0);
      el.currentTime = 0;
    };
    el.addEventListener('timeupdate', ajustar);
  };

  const alternar = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => setErro('Não foi possível tocar o áudio.'));
    else el.pause();
  };

  const buscar = (valor: number) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = valor;
    setAtual(valor);
  };

  const progresso = duracao > 0 ? Math.min(100, (atual / duracao) * 100) : 0;

  return (
    <div className={`${styles.player} ${tocando ? styles.playerTocando : ''}`}>
      {srcCarregado && (
        <audio
          ref={audioRef}
          src={srcCarregado}
          preload="metadata"
          onLoadedMetadata={aoCarregarMetadados}
          onDurationChange={aoCarregarMetadados}
          onTimeUpdate={() => setAtual(audioRef.current?.currentTime || 0)}
          onPlay={aoIniciar}
          onPause={aoParar}
          onEnded={() => { aoParar(); setAtual(0); }}
          onError={() => setErro('Não foi possível carregar o áudio.')}
        />
      )}
      <div className={styles.playerLinha}>
        <button
          type="button"
          className={styles.playerBotao}
          onClick={alternar}
          disabled={!srcCarregado}
          aria-label={tocando ? 'Pausar' : 'Tocar'}
        >
          {tocando ? '❚❚' : '▶'}
        </button>
        <input
          type="range"
          className={styles.playerBarra}
          min={0}
          max={duracao || 0}
          step={0.1}
          value={Math.min(atual, duracao || 0)}
          onChange={(e) => buscar(Number(e.target.value))}
          disabled={!srcCarregado || !duracao}
          aria-label="Posição do áudio"
          style={{ background: `linear-gradient(to right, var(--accent, #0284c7) ${progresso}%, #d6dde6 ${progresso}%)` }}
        />
        <span className={styles.playerTempo}>{formatarTempo(atual)} / {formatarTempo(duracao)}</span>
      </div>
      {erro && <div className={styles.playerErro}>{erro}</div>}

      {falhaTranscricao && (
        <div className={styles.transcricaoFalha}>Não foi possível transcrever este áudio. Ele continua disponível para ouvir.</div>
      )}
      {!falhaTranscricao && transcricao && (
        <>
          <button type="button" className={styles.botaoTranscricao} onClick={() => setTranscricaoAberta((v) => !v)}>
            {transcricaoAberta ? 'Ocultar transcrição' : 'Ver transcrição'}
          </button>
          {transcricaoAberta && <div className={styles.transcricaoTexto}>{transcricao}</div>}
        </>
      )}
    </div>
  );
};

export default PlayerAudio;
